import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button, Input, Select, Textarea, Field } from "@/components/ds";
import api, { apiErr } from "@/lib/api";
import useIsMobile from "@/hooks/useIsMobile";

const WASHES = ["Indigo", "Black / Black OD", "Stone wash", "Raw / Rinse", "Not sure yet"];
const ENQUIRY = ["Bulk order", "Sample first, then order", "Exploring pricing"];
const COMPANY_TYPES = ["Buying House", "Brand", "Garment manufacturer"];

const empty = { name: "", company: "", company_type: "", phone: "", email: "", quantity: "", wash: "", article: "", enquiry_type: "", size_ratio: "" };

export default function EnquiryModal({ open, product, intent, region, onClose }) {
  const isMobile = useIsMobile();
  const gcols = isMobile ? "1fr" : "repeat(2,minmax(0,1fr))";
  const isBd = region === "dhaka";
  const namePh = isBd ? "Shakib Aftab" : "Rahul Sharma";
  const phonePh = isBd ? "+880 1712 345678" : "+91 98765 43210";
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setForm({ ...empty, article: product?.article || "", enquiry_type: intent === "sample" ? "Sample first, then order" : "Bulk order" });
    }
  }, [open, product, intent]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isSample = intent === "sample";
  const title = isSample ? "Request a sample" : "Place a query";
  const sub = isSample
    ? "Samples ship in 7–10 days. Fabric is capped at 10 m per denim item."
    : "Tell us the quantity and wash — we come back with landed pricing.";

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.phone.trim()) return toast.error("Please enter a phone number");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email");
    setBusy(true);
    try {
      await api.post("/leads", {
        ...form,
        sku_code: product?.code || "",
        sku_name: product?.name || "",
        intent: intent || "bulk",
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="enquiry-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(20,19,28,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="lf-fade-up" style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", background: "var(--surface-card)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-floating)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, padding: "20px 24px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <div>
            <div style={{ font: "var(--fw-medium) 20px/1.2 var(--font-display)", color: "var(--text-primary)" }}>{title}</div>
            <div style={{ marginTop: 5, font: "var(--fw-regular) 13px/1.4 var(--font-sans)", color: "var(--text-secondary)" }}>{sub}</div>
          </div>
          <span data-testid="modal-close-btn" role="button" tabIndex={0} onClick={onClose} style={{ cursor: "pointer", fontSize: 22, lineHeight: 1, color: "var(--text-tertiary)" }}>×</span>
        </div>

        {product && (
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 24px", background: "var(--surface-muted)", borderBottom: "1px solid var(--border-default)" }}>
            <span style={{ width: 48, height: 48, flexShrink: 0, borderRadius: "var(--radius-md)", background: "var(--lf-blue-900)", backgroundImage: product.images?.[0] ? `url(${product.images[0]})` : "var(--texture-weave-light)", backgroundSize: "cover", backgroundPosition: "center" }} />
            <div>
              <div style={{ font: "var(--fw-medium) 14px/1.2 var(--font-sans)", color: "var(--text-primary)" }}>{product.name}</div>
              <div style={{ marginTop: 4, font: "var(--fw-regular) 12px/1.3 var(--font-mono)", color: "var(--text-secondary)" }}>{product.code} · {product.fabric} · {product.weight} · {product.priceLabel}/pc · MOQ {product.moqLabel}</div>
            </div>
          </div>
        )}

        {!submitted ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: gcols, gap: 12 }}>
                <Field label="Your name"><Input data-testid="lead-name" placeholder={namePh} value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
                <Field label="Company Name"><Input data-testid="lead-company" placeholder="Brand or buying house" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: gcols, gap: 12 }}>
                <Field label="Company type"><Select data-testid="lead-company-type" placeholder="Select type" options={COMPANY_TYPES} value={form.company_type} onChange={(e) => set("company_type", e.target.value)} /></Field>
                <Field label="Type of enquiry"><Select data-testid="lead-enquiry-type" options={ENQUIRY} value={form.enquiry_type} onChange={(e) => set("enquiry_type", e.target.value)} /></Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: gcols, gap: 12 }}>
                <Field label="Phone"><Input data-testid="lead-phone" type="tel" placeholder={phonePh} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Email"><Input data-testid="lead-email" type="email" placeholder="you@company.com" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: gcols, gap: 12 }}>
                <Field label="Quantity (pcs)"><Input data-testid="lead-quantity" type="text" inputMode="numeric" placeholder="500" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
                <Field label="Preferred wash"><Select data-testid="lead-wash" placeholder="Select wash" options={WASHES} value={form.wash} onChange={(e) => set("wash", e.target.value)} /></Field>
              </div>
              {isSample && (
                <div style={{ display: "grid", gridTemplateColumns: gcols, gap: 12 }}>
                  <Field label="Article no."><Input data-testid="lead-article" placeholder="ART-0000" value={form.article} onChange={(e) => set("article", e.target.value)} /></Field>
                </div>
              )}
              <Field label="Size ratio / requirements">
                <Textarea data-testid="lead-size-ratio" placeholder="e.g. 28-30-32-34-36 in equal ratio, contrast stitch on back pockets" value={form.size_ratio} onChange={(e) => set("size_ratio", e.target.value)} />
              </Field>
            </div>
            <div style={{ padding: "16px 24px 22px", borderTop: "1px solid var(--border-default)" }}>
              <Button data-testid="submit-lead-btn" variant="primary" size="md" fullWidth onClick={submit} disabled={busy}>{busy ? "Sending…" : "Send query"}</Button>
              <p style={{ margin: "10px 0 0", textAlign: "center", font: "var(--fw-regular) 12px/1.4 var(--font-sans)", color: "var(--text-tertiary)" }}>We respond within 24 hours · Your details stay with Locofast</p>
            </div>
          </>
        ) : (
          <div data-testid="lead-success" style={{ padding: "44px 32px", textAlign: "center" }}>
            <div style={{ width: 52, height: 52, margin: "0 auto 16px", borderRadius: "50%", background: "var(--lf-emerald-50)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
            <div style={{ font: "var(--fw-medium) 20px/1.2 var(--font-display)", color: "var(--lf-emerald-700)" }}>Query received</div>
            <p style={{ margin: "10px auto 0", maxWidth: "38ch", font: "var(--fw-regular) 14px/1.6 var(--font-sans)", color: "var(--text-secondary)" }}>Our sourcing team will come back within 24 hours with pricing at your quantity, wash options and the sample timeline.</p>
          </div>
        )}
      </div>
    </div>
  );
}
